import { FC, useEffect, useMemo, useState } from 'react';
import { ZoneTag } from 'src/components/ui/ZoneTag';
import { Button } from 'src/components/ui/Button';
import { useAuth } from 'src/hooks/useAuth';
import { fetchAllInfo } from 'src/store/categories/actions';
import { useAppDispatch, useAppSelector } from 'src/hooks/useRedux';
import { CategoryType, PolygonsFilterType } from 'src/config/types';
import CategoriesService from 'src/services/CategoriesService';
import { sleep } from 'src/utils';
import { Search } from 'src/components/ui/Search';

interface GroupsFilterProps {
  handleSelect: (id: number, filterName: keyof PolygonsFilterType) => void;
  smoothScrollTo: (id: string) => void;
  tempFilter: PolygonsFilterType;
}

export const GroupsFilter: FC<GroupsFilterProps> = ({
  handleSelect,
  tempFilter,
  smoothScrollTo,
}) => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const { categories } = useAppSelector(state => state.categories);

  const [tempCategories, setTempCategories] = useState<CategoryType[]>(categories);
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  const [showAll, setShowAll] = useState<boolean>(false);
  const [filter, setFilter] = useState<string>('');

  const handleScroll = async () => {
    await sleep(100);
    smoothScrollTo('filter-group');
  };

  const handleOffEditMode = () => setIsEditMode(false);
  const handleShawAll = async () =>
    setShowAll(prev => {
      !prev && handleScroll();

      return !prev;
    });

  useEffect(() => {
    setTempCategories(categories);
  }, [categories]);

  const handleSaveTempZoneTypes = async () => {
    const categoriesWithFlag: CategoryType[] = [];

    try {
      tempCategories.forEach(category => {
        if (!category.name || !category.flag) return;
        if (category?.flag === 'delete' && !category?.id) return;

        categoriesWithFlag.push(category);
      });

      setTempCategories([]);
      await CategoriesService.insertStatuses(categoriesWithFlag);

      dispatch(fetchAllInfo(!!user?.is_colorpicker));
    } catch (e) {
      console.log(e.message);
    } finally {
      handleOffEditMode();
    }
  };

  const handleOnEditMode = () => {
    setTempCategories(categories);
    setIsEditMode(true);
    handleScroll();
  };

  const handleChangeTempZoneType = (category: CategoryType, index: number) =>
    setTempCategories(prev => {
      const temp = [...prev];
      temp[index] = category;
      return temp;
    });

  const handleAddTempCategory = () => {
    !isEditMode && handleOnEditMode();
    setTempCategories(prev => [
      ...prev,
      {
        name: '',
        color: '#D8DCDE',
        flag: 'insert',
      },
    ]);
  };

  const list = useMemo(() => {
    if (filter) {
      return tempCategories.filter(category =>
        category?.name?.toLowerCase()?.includes(filter?.toLowerCase()),
      );
    }

    if (showAll || isEditMode) {
      return tempCategories;
    }

    return tempCategories.slice(0, 8);
  }, [tempCategories, showAll, isEditMode, filter]);

  return (
    <div className="w-full flex flex-col gap-4">
      <div className="absolute -top-1 right-0 w-32 m-md:w-40">
        <Search variant="extraSmall" onChange={setFilter} />
      </div>

      <div
        className={
          't-sm:grid t-sm:grid-cols-3 flex flex-wrap gap-x-2 t-sm:gap-x-4 gap-y-2.5 font-gothampro-500'
        }
      >
        {list.map((category, i) => {
          if (category.flag === 'delete') return;

          const isActive = tempFilter?.categories?.includes(category.id || 0);

          return (
            <ZoneTag
              withColor={!!user?.is_colorpicker}
              colorBottom={i <= 2}
              isActive={isActive}
              isEditMode={isEditMode}
              onChange={handleChangeTempZoneType}
              fitWidth
              index={i}
              onClick={() => category.id && handleSelect(category.id, 'categories')}
              key={`category-filter-${category.name}`}
              category={{ ...category, color: user?.is_colorpicker ? category.color : '#FF5500' }}
            />
          );
        })}

        {!isEditMode && !filter && (
          <ZoneTag
            isActive
            noShadow
            whiteText
            fullWidth
            withColor={!!user?.is_colorpicker}
            onClick={handleShawAll}
            category={{ name: showAll ? 'Скрыть' : 'Показать все', color: '#FF5500' }}
          />
        )}
      </div>

      {user?.isManager ? (
        <div className="w-full flex t-sm:flex-row flex-col justify-center gap-2 t-sm:gap-5">
          <Button
            variant="orange"
            classNames="w-full t-sm:w-44"
            onClick={isEditMode ? handleSaveTempZoneTypes : handleOnEditMode}
          >
            {isEditMode ? 'Сохранить' : 'Редактировать'}
          </Button>
          <Button variant="blue" classNames="w-full t-sm:w-44" onClick={handleAddTempCategory}>
            Добавить новую группу
          </Button>
        </div>
      ) : null}
    </div>
  );
};
